/* eslint-disable complexity */
import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  ACCOUNTING_TAG,
  createDatasourceByIntegration,
  filterIntregationsByTag,
  getOnboardingStartUrl,
  IntegrationStart,
  PREDICT_PRODUCT_ID,
  TechnicalIssues
} from '@futrli/shared-services';
import { activateProduct, createOrg, createProviderConnection, getCallbackUrl } from '@futrli/organisations';
import { BillingModel, Integration } from '@futrli-sdk/app-store';
import { CreateOrganisationUploadDrawer } from '@futrli/shared-services/excel-upload/excel-modals/upload/create-organisation-upload-drawer';
import { Organisation } from '@futrli-sdk/organisations';
import { StoreState } from '@futrli/auth';
import { UIDefaultErrorMessage } from '@futrli/ui-components';

import { OnboardingNavbar } from './onboarding-navbar';
import { trackEvent } from '../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../predict/src/core/constants';

import * as styles from './onboarding.scss';

interface Props {
  readonly workspaceId: string;
  readonly organisation: Organisation | null;
  readonly integrations: Integration[];
}

export const OnboardingIntegrations = ({ workspaceId, organisation, integrations }: Props) => {
  const intl = useIntl();
  const [error, setError] = useState(false);
  const [isOpenExcelSidebar, setIsOpenExcelSidebar] = useState(false);

  const [activeIntegrationId, setActiveIntegrationId] = useState<string>();

  const userId = useSelector((store: StoreState) => {
    const { authReducers } = store;
    return authReducers.authenticatedUser.username;
  });

  const tempSupportedProviders = ['sage', 'xero', 'qbo', 'excel'];

  const filteredIntegrations = [];

  const accountingIntegrations = filterIntregationsByTag(integrations, ACCOUNTING_TAG);

  const demoIntegrations = useMemo(() => integrations.filter(i => i.slug === 'demo-au' || i.slug === 'demo-eu'), [
    integrations
  ]);

  for (const providerSlug of tempSupportedProviders) {
    const integration = (providerSlug === 'excel' ? integrations : accountingIntegrations)
      .find(candidate => candidate.slug === providerSlug);
    if (integration) {
      filteredIntegrations.push(integration);
    }
  }

  const handleCloseExcelSidebar = useCallback(() => setIsOpenExcelSidebar(false), []);

  const handleProviderClick = useCallback(
    async (integration?: Integration) => {
      if (!integration) {
        return;
      }
      if (integration.slug === 'excel') {
        setIsOpenExcelSidebar(true);
        return;
      }
      setError(false);
      const isDemoOrg = integration.slug === 'demo-au' || integration.slug === 'demo-eu';
      setActiveIntegrationId(integration.id);

      try {
        const ds = createDatasourceByIntegration(integration.id, integrations);
        const tmpOrg = { name: 'Organisation', datasource: ds };
        const org = await createOrg(workspaceId, tmpOrg);

        await activateProduct(workspaceId, org.id, PREDICT_PRODUCT_ID, BillingModel.free);

        const response = await createProviderConnection(workspaceId, org.id, {
          workspace_id: workspaceId,
          organisation_id: org.id,
          datasource_id: org.datasource.id,
          integration:
            integration.slug === 'demo-au' ? 'demo_au' : integration.slug === 'demo-eu' ? 'demo_eu' : integration.slug,
          user_id: userId,
          provider: isDemoOrg ? 'demo' : integration.slug === 'xero' ? 'xero' : 'ledgerflow',
          success_url: getCallbackUrl(getOnboardingStartUrl())
        });

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          // markAsStale(orgSchema.key);
          switch (integration.slug) {
            case 'demo-au':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_AU_ORGANISATION);
            case 'demo-eu':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_UK_ORGANISATION);
            case 'qbo':
              trackEvent(TRACKING_EVENTS.ADDED_QBO_ORGANISATION);
            case 'sage':
              trackEvent(TRACKING_EVENTS.ADDED_SAGE_ORGANISATION);
            default:
              trackEvent(TRACKING_EVENTS.ADDED_XERO_ORGANISATION);
          }
          window.location.href = response.initiation_url;
        }
      } catch (e) {
        setError(true);
        setActiveIntegrationId(undefined);
      }
    },
    [integrations, userId, workspaceId]
  );

  if (!filteredIntegrations.length && !demoIntegrations.length) {
    return <UIDefaultErrorMessage />;
  }

  return (
    <>
      <OnboardingNavbar />
      <div className={styles.container}>
        <div style={{ height: 200 }} />
        <h1 className={styles.title} style={{ marginBottom: 10 }}>
          <FormattedMessage id="onboarding.lets_connect" defaultMessage="Let’s connect to your accounting software." />
        </h1>
        <div className={styles.subTitle}>
          <FormattedMessage
            id="onboarding.authorize_and_back"
            defaultMessage="Hit one of the connect buttons below. You’ll be taken to authorise and brought back."
          />
        </div>
        <div style={{ height: 10 }} />
        <div className={styles.introImgContainerIntegrations}>
          {filteredIntegrations.map(integration => {
            if (organisation?.datasource.integration_id && organisation?.datasource.integration_id !== integration.id) {
              return null;
            }

            return (
              <IntegrationStart
                error={error}
                key={integration.id}
                integration={integration}
                activeIntegrationId={activeIntegrationId}
                onClick={handleProviderClick}
                connectTextOverride={
                  integration.slug === 'excel' ? (
                    <FormattedMessage id="predict.upload_excel" defaultMessage="Upload Excel" />
                  ) : undefined
                }
              />
            );
          })}
          {demoIntegrations.length && (
            <div
              className={classNames(styles.demoText, styles.demoTextLineHeight, {
                [styles.disabledContainer]: typeof activeIntegrationId === 'string'
              })}
              style={{ marginTop: 100 }}
            >
              <FormattedMessage
                id="onboarding.get_started_demo_click_to_connect"
                defaultMessage="Or get started with demo data. Click to connect {demoUK} or {demoAU}. Demo organisations will not affect the licences in your subscription."
                values={{
                  demoUK: (
                    <span
                      className={classNames(styles.demoCompanyName, {
                        [styles.disabledDemoText]: typeof activeIntegrationId === 'string'
                      })}
                      onClick={() => handleProviderClick(demoIntegrations.find(i => i.slug === 'demo-eu'))}
                    >
                      {intl.formatMessage({ id: 'onboarding.demo_comp_(uk)', defaultMessage: 'Demo Company (UK)' })}
                    </span>
                  ),
                  demoAU: (
                    <span
                      className={classNames(styles.demoCompanyName, {
                        [styles.disabledDemoText]: typeof activeIntegrationId === 'string'
                      })}
                      onClick={() => handleProviderClick(demoIntegrations.find(i => i.slug === 'demo-au'))}
                    >
                      {intl.formatMessage({ id: 'onboarding.demo_comp_(au)', defaultMessage: 'Demo Company (AU)' })}
                    </span>
                  )
                }}
              />
            </div>
          )}
        </div>
        {error && (
          <div style={{ marginTop: 16 }}>
            <TechnicalIssues />
          </div>
        )}
      </div>
      <CreateOrganisationUploadDrawer
        isOpen={isOpenExcelSidebar}
        onClose={handleCloseExcelSidebar}
        workspaceId={workspaceId}
        integration={integrations.find(i => i.slug === 'excel')}
        isOnboarding
      />
    </>
  );
};
